import * as React from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Divider,
  Avatar,
  Autocomplete,
  TextField,
  FormHelperText,
  OutlinedInput,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
//import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import LanguageIcon from "@mui/icons-material/Language";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
  colors,
  typography,
  spacing,
} from "../../../../../../../assets/styles/tokens";
import AddTasks from "./AddTrendTasks";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { useSelector } from "react-redux";
import {
  selectAllUsers,
  selectUserWebsitePermissions,
} from "../../../../../../../features/userSlice";
import ClearIcon from "@mui/icons-material/Clear";
import { useState } from "react";

export interface SharedTaskProps {
  assignee: string;
  dueDate: Dayjs | null;
  priority: string;
  website: string;
}

interface CreateTasksDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function CreateTasksDrawer({
  open,
  onClose,
}: CreateTasksDrawerProps) {
  const [step, setStep] = React.useState<"shared" | "addTasks">("shared");
  const [hasVisitedAddTasks, setHasVisitedAddTasks] = React.useState(false);
  const allUsers = useSelector(selectAllUsers);
  const websitePermissions = useSelector(selectUserWebsitePermissions) as any;
  const [tasks, setTasks] = React.useState<any[]>([]);
  const [openSearch, setOpenSearch] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const initialData = {
    assignee: {
      id: "",
      name: "",
    },
    due_date: "",
    priority: "",
    website: {
      id: "",
      name: "",
    },
  };

  const [sharedPropertiesForCreateTask, setSharedPropertiesForCreateTask] =
    React.useState<any>(initialData);
  const [formError, setFormError] = React.useState<any>({
    assignee: "",
    due_date: "",
    priority: "",
    website: "",
  });

  const siteOptions =
    websitePermissions?.map((siteObj: any) => {
      const uid = Object.keys(siteObj)[0]; // UID of the site
      const siteName = siteObj[uid].ui_site_name; // Site name
      const dimensions = siteObj[uid]?.dimensions; // Site dimensions
      return { uid, siteName, dimensions }; // Return as an object with uid and siteName
    }) || [];

  const selectedUser =
    allUsers?.find(
      (user: any) =>
        !!sharedPropertiesForCreateTask?.assignee?.id && // ✅ guard: skip find if id is empty/null
        user.id === sharedPropertiesForCreateTask?.assignee?.id,
    ) ?? null;

  // Handle input change
  const handleInputChange = (name: string, value: any) => {
    let updatedValue = value;

    if (name === "assignee") {
      const user = allUsers?.find((u: any) => u?.id === value);
      updatedValue = {
        id: value || "",
        name: user
          ? `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim()
          : "",
      };
    }

    if (name === "website") {
      const site = siteOptions?.find((s: any) => s?.uid === value);
      updatedValue = {
        id: value || "",
        name: site?.siteName || "",
      };
    }

    setSharedPropertiesForCreateTask({
      ...sharedPropertiesForCreateTask,
      [name]: updatedValue,
    });
  };

  React.useEffect(() => {
    if (!open) {
      setStep("shared");
      setHasVisitedAddTasks(false);
      setSharedPropertiesForCreateTask(initialData);
      setTasks([]);
      setFormError({
        assignee: "",
        due_date: "",
        priority: "",
        website: "",
      });
    }
    // eslint-disable-next-line
  }, [open]);

  // All four shared fields are filled
  const allSharedFilled =
    sharedPropertiesForCreateTask?.assignee?.id !== "" ||
    sharedPropertiesForCreateTask?.due_date !== "" ||
    sharedPropertiesForCreateTask?.priority !== "" ||
    sharedPropertiesForCreateTask?.website?.id !== "";

  const buttonLabel =
    hasVisitedAddTasks && allSharedFilled
      ? "SAVE" // visited AddTasks AND at least one field still has a value
      : allSharedFilled
        ? "NEXT" // never visited but something is filled
        : "SKIP";

  const handleProceed = () => {
    setHasVisitedAddTasks(true);
    if (tasks.length === 0) {
      setTasks([
        {
          itemId: 1,
          task_name: "",
          category: "",
          target: "",
          referTo: "",
          website: sharedPropertiesForCreateTask?.website?.id || "",
          due_date: sharedPropertiesForCreateTask?.due_date || null,
          assignee: sharedPropertiesForCreateTask?.assignee?.id || "",
          priority: sharedPropertiesForCreateTask?.priority || "",
        },
      ]);
    }
    setStep("addTasks");
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            display: "flex",
            flexDirection: "column",
            pb: spacing.Spacing.xl.$value,
          },
        }}
      >
        {step === "shared" ? (
          <>
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: spacing.Spacing.xl.$value,
                px: spacing.Spacing.xl.$value,
              }}
            >
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
              <Typography
                sx={{
                  fontWeight: typography.Font_weight.Medium.$value,
                  fontSize: typography.Size.Text.xl.$value,
                  color: colors.GrayLightMode[900].$value,
                }}
              >
                Create Tasks
              </Typography>
              <Box sx={{ width: spacing.Spacing["4xl"].$value }} />
            </Box>
            <Divider />

            {/* Scrollable body */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: spacing.Spacing.xl.$value,
                pt: spacing.Spacing.xl.$value,
              }}
            >
              <Box
                sx={{
                  pt: spacing.Spacing.md.$value,
                  pb: spacing.Spacing["3xl1"].$value,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: typography.Font_weight.Medium.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    color: colors.GrayLightMode[900].$value,
                  }}
                >
                  Shared Properties (Optional)
                </Typography>
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.sm.$value,
                    color: colors.GrayDarkMode[700].$value,
                  }}
                >
                  Set defaults for all tasks, or skip to add tasks individually
                </Typography>
              </Box>
              {/* Assignee */}
              <FormControl fullWidth sx={{ mb: spacing.Spacing.xl.$value }}>
                <InputLabel
                  shrink
                  sx={{
                    color: colors.GrayLightMode[500].$value,
                    background: colors.Base.White.$value,
                    paddingRight: spacing.Spacing.xs.$value,
                    "&.Mui-focused": {
                      color: colors.Brand[600].$value,
                    },
                  }}
                >
                  Assignee
                </InputLabel>

                <Autocomplete
                  options={allUsers || []}
                  open={openSearch && !selectedUser}
                  value={selectedUser || null}
                  inputValue={
                    selectedUser
                      ? `${selectedUser?.first_name ?? ""} ${selectedUser?.last_name ?? ""}`.trim()
                      : inputValue
                  }
                  disableClearable // remove MUI default cross icon completely
                  onOpen={() => {
                    if (!selectedUser) setOpenSearch(true);
                  }}
                  onClose={(_, reason) => {
                    setOpenSearch(false);

                    // only act when user clicks outside or blur
                    if (reason === "blur" || reason === "escape") {
                      if (!selectedUser && inputValue) {
                        const hasMatch = allUsers.some((user: any) =>
                          `${user?.first_name ?? ""} ${user?.last_name ?? ""}`
                            .toLowerCase()
                            .includes(inputValue.toLowerCase()),
                        );

                        //No match → clear input
                        if (!hasMatch) {
                          setInputValue("");
                        }
                      }
                    }
                  }}
                  onInputChange={(_, value, reason) => {
                    if (selectedUser) return;

                    if (reason === "input") {
                      setInputValue(value);
                      setOpenSearch(true);
                    }
                  }}
                  onChange={(_, newValue) => {
                    handleInputChange("assignee", newValue?.id || "");
                    setOpenSearch(false);
                    setInputValue("");
                  }}
                  filterOptions={(options, { inputValue }) =>
                    (options || []).filter(
                      (
                        option, // fallback to []
                      ) =>
                        `${option?.first_name ?? ""} ${option?.last_name ?? ""}`
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()),
                    )
                  }
                  getOptionLabel={(option) =>
                    `${option?.first_name ?? ""} ${option?.last_name ?? ""}`.trim()
                  }
                  fullWidth
                  renderOption={(props, option) => (
                    <li {...props} key={option.id}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Avatar sx={{ width: 24, height: 24 }}>
                          {option?.first_name?.charAt(0)?.toUpperCase() ?? "?"}
                        </Avatar>
                        {option?.first_name} {option?.last_name}
                      </Box>
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assignee"
                      size="small"
                      color="secondary"
                      sx={{
                        // ✅ Override focus border color
                        "& .MuiOutlinedInput-root": {
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: colors.Brand[600].$value,
                          },
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: colors.Brand[600].$value,
                          },
                        },
                        // ✅ Override label color on focus
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: colors.Brand[600].$value,
                        },
                      }}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: !!selectedUser,
                        style: {
                          caretColor: selectedUser ? "transparent" : undefined,
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,

                        startAdornment: selectedUser ? (
                          <Avatar sx={{ width: 24, height: 24, mr: 1 }}>
                            {selectedUser?.first_name
                              ?.charAt(0)
                              ?.toUpperCase() ?? "?"}
                          </Avatar>
                        ) : null,

                        endAdornment: (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            {/* ONLY ONE clear icon */}
                            {(selectedUser || inputValue) && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleInputChange("assignee", "");
                                  setInputValue("");
                                }}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            )}

                            {/* Always keep dropdown */}
                            {params.InputProps.endAdornment}
                          </Box>
                        ),
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !selectedUser) {
                          e.preventDefault();

                          const filtered = (allUsers || []).filter(
                            (
                              user: any, // fallback to []
                            ) =>
                              `${user?.first_name ?? ""} ${user?.last_name ?? ""}`
                                .toLowerCase()
                                .includes(inputValue.toLowerCase()),
                          );

                          if (filtered.length > 0) {
                            handleInputChange("assignee", filtered[0].id);
                            setOpenSearch(false);
                            setInputValue("");
                          }
                        }
                      }}
                    />
                  )}
                />
              </FormControl>

              {/* Due Date */}
              <DatePicker
                value={
                  sharedPropertiesForCreateTask?.due_date
                    ? dayjs(sharedPropertiesForCreateTask.due_date)
                    : null
                }
                minDate={dayjs()} // Prevent selection of past dates
                disablePast // Disables all past dates automatically
                onChange={(date) => {
                  handleInputChange(
                    "due_date",
                    date ? dayjs(date).format("YYYY-MM-DD HH:mm:ss") : "",
                  );
                }}
                format="DD/MM/YYYY"
                slots={{
                  openPickerIcon: CalendarTodayIcon,
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    label: "Due Date",
                    placeholder: "dd/mm/yyyy",
                    error: !!formError?.due_date,
                    helperText: formError?.due_date || "",
                    sx: {
                      mb: spacing.Spacing.xl.$value,
                      "& .MuiOutlinedInput-root": {
                        borderRadius: spacing.Radius.xs.$value,
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.Brand[600].$value,
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: colors.Brand[600].$value,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: colors.Brand[600].$value,
                      },
                      fontSize: typography.Size.Text.sm.$value,
                    },
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarTodayIcon
                            fontSize="small"
                            sx={{
                              color: colors.GrayDarkMode[700].$value,
                              mr: spacing.Spacing.xxs.$value,
                            }}
                          />
                        </InputAdornment>
                      ),
                      // FIX: show X only when a date is selected
                      endAdornment: sharedPropertiesForCreateTask?.due_date ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation(); // prevent picker from opening
                              handleInputChange("due_date", ""); // clear the value
                            }}
                          >
                            <ClearIcon
                              fontSize="small"
                              sx={{ color: colors.GrayDarkMode[700].$value }}
                            />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  },
                  openPickerButton: {
                    sx: { display: "none" },
                  },
                  actionBar: {
                    sx: {
                      "& .MuiButton-root": {
                        color: colors.Brand[700].$value,
                      },
                      "& .MuiButton-root:last-of-type": {
                        color: colors.Brand[600].$value,
                      },
                    },
                  },
                }}
              />
              {/* Priority */}
              <FormControl
                fullWidth
                sx={{ mb: spacing.Spacing.xl.$value }}
                error={!!formError?.priority}
              >
                <InputLabel
                  shrink
                  sx={{
                    color: colors.GrayLightMode[500].$value,
                    background: colors.Base.White.$value,
                    paddingRight: spacing.Spacing.xs.$value,
                    "&.Mui-focused": {
                      color: colors.Brand[600].$value,
                    },
                  }}
                >
                  Priority
                </InputLabel>

                <Select
                  value={sharedPropertiesForCreateTask?.priority || ""}
                  onChange={(e) =>
                    handleInputChange("priority", e.target.value)
                  }
                  displayEmpty
                  notched
                  label="Priority"
                  size="small"
                  input={
                    <OutlinedInput
                      notched
                      label="Priority"
                      size="small"
                      endAdornment={
                        sharedPropertiesForCreateTask?.priority ? (
                          <InputAdornment position="end" sx={{ mr: "20px" }}>
                            {" "}
                            {/* FIX: was "4px", now "20px" to clear the chevron */}
                            <IconButton
                              size="small"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInputChange("priority", "");
                              }}
                              edge="end"
                            >
                              <ClearIcon
                                fontSize="small"
                                sx={{ color: colors.GrayDarkMode[700].$value }}
                              />
                            </IconButton>
                          </InputAdornment>
                        ) : null
                      }
                    />
                  }
                  renderValue={(selected) => {
                    const priorityConfig: Record<
                      string,
                      { icon: React.ReactNode; label: string }
                    > = {
                      high: {
                        icon: (
                          <ExpandLessIcon
                            fontSize="medium"
                            sx={{ color: colors.Error[500].$value }}
                          />
                        ),
                        label: "High",
                      },
                      medium: {
                        icon: (
                          <DragHandleIcon
                            fontSize="medium"
                            sx={{ color: colors.Warning[500].$value }}
                          />
                        ),
                        label: "Medium",
                      },
                      low: {
                        icon: (
                          <ExpandMoreIcon
                            fontSize="medium"
                            sx={{ color: colors.Blue[500].$value }}
                          />
                        ),
                        label: "Low",
                      },
                      highest: {
                        icon: (
                          <KeyboardDoubleArrowUpIcon
                            fontSize="medium"
                            sx={{ color: colors.Error[500].$value }}
                          />
                        ),
                        label: "Highest",
                      },
                      lowest: {
                        icon: (
                          <KeyboardDoubleArrowDownIcon
                            fontSize="medium"
                            sx={{ color: colors.Blue[500].$value }}
                          />
                        ),
                        label: "Lowest",
                      },
                    };
                    const config = priorityConfig[selected as string];
                    return config ? (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.Spacing.md.$value,
                        }}
                      >
                        {config.icon}
                        <Typography
                          sx={{
                            fontSize: typography.Size.Text.sm.$value,
                            color: colors.GrayLightMode[900].$value,
                          }}
                        >
                          {config.label}
                        </Typography>
                      </Box>
                    ) : (
                      // No selection → show placeholder with list icon
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.Spacing.md.$value,
                        }}
                      >
                        <FormatListBulletedIcon
                          fontSize="small"
                          sx={{ color: colors.GrayDarkMode[700].$value }}
                        />
                        <Typography
                          sx={{
                            fontSize: typography.Size.Text.sm.$value,
                            color: colors.GrayLightMode[900].$value,
                          }}
                        >
                          Select Priority
                        </Typography>
                      </Box>
                    );
                  }}
                  sx={{
                    borderRadius: spacing.Radius.xs.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    "& .MuiSelect-select": {
                      paddingTop: spacing.Spacing.md1.$value,
                      paddingBottom: spacing.Spacing.md1.$value,
                      color: colors.GrayLightMode[900].$value,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                  }}
                >
                  {[
                    {
                      value: "high",
                      label: "High",
                      icon: (
                        <ExpandLessIcon
                          fontSize="medium"
                          sx={{ color: colors.Warning[500].$value }}
                        />
                      ),
                    },
                    {
                      value: "medium",
                      label: "Medium",
                      icon: (
                        <DragHandleIcon
                          fontSize="medium"
                          sx={{ color: colors.Warning[500].$value }}
                        />
                      ),
                    },
                    {
                      value: "low",
                      label: "Low",
                      icon: (
                        <ExpandMoreIcon
                          fontSize="medium"
                          sx={{ color: colors.Blue[500].$value }}
                        />
                      ),
                    },
                    {
                      value: "highest",
                      label: "Highest",
                      icon: (
                        <KeyboardDoubleArrowUpIcon
                          fontSize="medium"
                          sx={{ color: colors.Error[500].$value }}
                        />
                      ),
                    },
                    {
                      value: "lowest",
                      label: "Lowest",
                      icon: (
                        <KeyboardDoubleArrowDownIcon
                          fontSize="medium"
                          sx={{ color: colors.Blue[500].$value }}
                        />
                      ),
                    },
                  ].map(({ value, label, icon }) => (
                    <MenuItem key={value} value={value}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: spacing.Spacing.md.$value,
                        }}
                      >
                        {icon}
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {formError?.priority && (
                  <FormHelperText>{formError?.priority}</FormHelperText>
                )}
              </FormControl>

              {/* Website */}
              <FormControl fullWidth error={!!formError?.website}>
                <InputLabel
                  sx={{
                    color: colors.GrayLightMode[500].$value,
                    background: colors.Base.White.$value,
                    paddingRight: spacing.Spacing.xs.$value,
                    "&.Mui-focused": {
                      color: colors.Brand[600].$value,
                    },
                  }}
                >
                  Website (optional)
                </InputLabel>
                <Select
                  value={sharedPropertiesForCreateTask?.website?.id || ""}
                  onChange={(e) => handleInputChange("website", e.target.value)}
                  displayEmpty
                  label="Website (optional)"
                  size="small"
                  input={
                    <OutlinedInput
                      notched
                      label="Website (optional)"
                      size="small"
                      endAdornment={
                        sharedPropertiesForCreateTask?.website?.id ? (
                          <InputAdornment position="end" sx={{ mr: "20px" }}>
                            {" "}
                            {/* FIX: was "4px", now "20px" to clear the chevron */}
                            <IconButton
                              size="small"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleInputChange("website", "");
                              }}
                              edge="end"
                            >
                              <ClearIcon
                                fontSize="small"
                                sx={{ color: colors.GrayDarkMode[700].$value }}
                              />
                            </IconButton>
                          </InputAdornment>
                        ) : null
                      }
                    />
                  }
                  startAdornment={
                    <InputAdornment position="start">
                      <LanguageIcon
                        fontSize="medium"
                        sx={{ color: colors.GrayDarkMode[700].$value }}
                      />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: spacing.Radius.xs.$value,
                    fontSize: typography.Size.Text.sm.$value,
                    "& .MuiSelect-select": {
                      paddingTop: spacing.Spacing.md1.$value,
                      paddingBottom: spacing.Spacing.md1.$value,
                      color: colors.GrayLightMode[900].$value,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.Brand[600].$value,
                    },
                  }}
                >
                  <MenuItem value="">Select Website</MenuItem>
                  {siteOptions?.map((site: any) => (
                    <MenuItem key={site.uid} value={site.uid}>
                      {site.siteName}
                    </MenuItem>
                  ))}
                </Select>
                {formError?.website && (
                  <FormHelperText>{formError?.website}</FormHelperText>
                )}
              </FormControl>

              {/* Info banner */}
              <Box
                sx={{
                  backgroundColor: colors.Brand[50].$value,
                  border: `1px solid ${colors.Brand[500].$value}`,
                  borderRadius: spacing.Radius.md.$value,
                  padding: `${spacing.Spacing.md.$value} ${spacing.Spacing.xl.$value}`,
                  marginBlock: spacing.Spacing.xl.$value,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: spacing.Spacing.md.$value,
                }}
              >
                <Typography
                  sx={{
                    fontSize: typography.Size.Text.xs.$value,
                    lineHeight: typography.Line_height.text.xs.$value,
                    color: colors.Brand[600].$value,
                    textAlign: "center",
                  }}
                >
                  💡 These values will apply to all tasks unless you override
                  them individually.
                </Typography>
              </Box>
            </Box>

            {/* SKIP / NEXT button pinned to bottom */}
            <Box sx={{ m: spacing.Spacing.xl.$value }}>
              <Button
                fullWidth
                variant="contained"
                // onClick={() =>
                //   buttonLabel === "SAVE" ? onClose() : handleProceed()
                // }
                onClick={handleProceed}
                sx={{
                  backgroundColor: colors.Brand[600].$value,
                  borderRadius: spacing.Radius.xs.$value,
                  textTransform: "none",
                  fontWeight: typography.Font_weight.Medium.$value,
                  fontSize: typography.Size.Text.sm.$value,
                  lineHeight: typography.Line_height.text.md.$value,
                  py: spacing.Spacing.md.$value,
                  "&:hover": { backgroundColor: colors.Brand[600].$value },
                }}
              >
                {buttonLabel}
              </Button>
            </Box>
          </>
        ) : (
          <AddTasks
            sharedProps={
              allSharedFilled ? sharedPropertiesForCreateTask : undefined
            }
            tasks={tasks}
            setTasks={setTasks}
            onBack={() => {
              // Reset only the fields covered by sharedProps
              setTasks((prev) =>
                prev.map((task) => ({
                  ...task,
                  // Reset only the fields covered by sharedProps
                  assignee: sharedPropertiesForCreateTask?.assignee?.id
                    ? ""
                    : task.assignee,
                  due_date: sharedPropertiesForCreateTask?.due_date
                    ? null
                    : task.due_date,
                  priority: sharedPropertiesForCreateTask?.priority
                    ? ""
                    : task.priority,
                  website: sharedPropertiesForCreateTask?.website?.id
                    ? ""
                    : task.website,
                })),
              );
              setStep("shared");
            }}
            onSave={() => onClose()}
          />
        )}
      </Drawer>
    </LocalizationProvider>
  );
}
